import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { LockKey, FloppyDisk } from "@phosphor-icons/react";

export default function PasswordPolicyTab() {
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const flash = (t, x) => { setMsg({ type: t, text: x }); setTimeout(() => setMsg({ type: "", text: "" }), 3500); };

  useEffect(() => { api.get("/settings/password-policy").then(r => setPolicy(r.data)).catch(() => {}); }, []);

  if (!policy) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setPolicy(p => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/password-policy", policy);
      setPolicy(r.data);
      flash("ok", "Password policy saved.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex items-center gap-2">
        <LockKey size={20} weight="bold" className="text-[#002FA7]" />
        <h2 className="font-display text-2xl font-black">Password policy</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        These rules apply to <b>new passwords</b> only — new user creation, self-service password changes, forgot-password resets, and admin-initiated resets. Existing users are not affected until they change their password.
      </p>
      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
        <div>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Minimum length</label>
          <input type="number" min="4" max="64" className="input" value={policy.min_length} onChange={(e) => set("min_length", Number(e.target.value))} data-testid="policy-min-length" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Password history (prevent reuse of last N)</label>
          <input type="number" min="0" max="20" className="input" value={policy.history_count} onChange={(e) => set("history_count", Number(e.target.value))} data-testid="policy-history" />
          <div className="text-[10px] text-muted-foreground mt-1">0 = disabled</div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Password expiry (days)</label>
          <input type="number" min="0" max="365" className="input" value={policy.expiry_days} onChange={(e) => set("expiry_days", Number(e.target.value))} data-testid="policy-expiry" />
          <div className="text-[10px] text-muted-foreground mt-1">0 = never expires</div>
        </div>
        <div className="md:col-span-2 space-y-2 border-t border-border pt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Character requirements</div>
          {[
            ["require_upper", "Require at least one uppercase letter (A–Z)"],
            ["require_lower", "Require at least one lowercase letter (a–z)"],
            ["require_number", "Require at least one digit (0–9)"],
            ["require_special", "Require at least one special character (!@#$…)"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 text-sm cursor-pointer">
              <input type="checkbox" checked={!!policy[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`policy-${k}`} />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="mt-8">
        <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="policy-save">
          <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save policy"}
        </button>
      </div>
    </div>
  );
}
